/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import { skipToken } from '@reduxjs/toolkit/query';
import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { mustNotExceed, numberIsGreaterThan } from 'components/forms/inputs/rhf-validators/validators';
import { myCasesFilter, openCasesFilter } from 'components/tables/list-table/filter-pieces/filterConstants';
import { formatToHours } from 'components/util/HoursDisplay';
import { format, getISODay, isBefore, parseISO, startOfISOWeek, subDays, subWeeks } from 'date-fns';
import useAuth from 'hooks/useAuth.ts';
import { ChangeEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { useGetMinAssignmentsListWithFiltersQuery } from 'store/api/assignments';
import { useGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useGetMatterByIdQuery, useGetMatterRestrictedInfoQuery } from 'store/api/matters';
import { useGetLastAddedTimecardPracticeAreasQuery, useGetLastTimecardCreatedAtQuery } from 'store/api/timecards';
import { MatterAssignmentMinDto } from 'types/assignments.ts';
import { EmployeeListItemMinDto } from 'types/employees';
import { FeatureFlag } from 'types/features.ts';
import { RemunerationCalcType } from 'types/matters.ts';
import { NamedEntityMinDto } from 'types/other';
import { DayOfWeek } from 'types/tenants.ts';
import { TimecardDetails, TimecardDto, TimecardMatterHistoryItem, TimecardStatusType } from 'types/timecards';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';
import { useCanReadMatterAssignments } from 'utils/permissionUtil';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { hoursToSeconds, secondsToHours } from 'utils/time';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import { useRequiredFields } from 'utils/useRequiredFields';
import {
    getIncrementDecimalScale,
    roundUpToIncrement,
    useTimecardIncrementResolver,
    useTimecardPrecision,
} from 'utils/useTimecardPrecision';
import { TemplateApplyMode } from 'components/buttons/TimecardTemplateButton';

export type TimecardFormProps = {
    type: 'add' | 'finish' | 'edit';
    initialData?: Partial<TimecardDto>;
    timecard?: TimecardDetails;
    /* overridden if initialData has employee set */
    prefillWithCurrentUser?: boolean;
    /**
     * The stored timecard, edited in its card on its record page: a single author, the stored seconds kept under
     * hours shown rounded to the case's increment, and the rules of a save that keeps the timecard's status.
     */
    inPlace?: boolean;
};

/**
 * What the form asks the assignments list for. The record page asks with the same arguments, so both read one
 * cache entry and the form opens with the list loaded.
 */
export const matterAssignmentsArgs = (matterId: number | string) => ({
    filters: `matter.id=in=(${matterId})`,
    sort: 'id,desc',
    pageSize: 1500,
});

/** The timecard form's state, values and handlers, shared by the full form page and the record's Timecard card. */
export const useTimecardForm = ({
    type = 'add',
    initialData,
    timecard,
    prefillWithCurrentUser,
    inPlace = false,
}: TimecardFormProps) => {
    const defaultValues: TimecardDto = useMemo(
        () => ({
            id: null,
            description: '',
            matter: null,
            employee: null,
            employees: [],
            timecardDate: format(new Date(), DEFAULT_DATE_FORMAT),
            isNonBillable: false,
            multiplierTypeCode: 'NONE',
            multiplierRate: '1.0',
            practiceAreas: [],
            totalTimeInSeconds: 0,
            totalTimeInHours: '0',
            billedTimeInSeconds: 0,
            billedTimeInHours: '0',
            expenses: [],
            matterAssignment: null,
            timecardActivityType: null,
            remunerationCalcType: RemunerationCalcType.EQUALS_BILLED_TIME,
            remunerationTimeInSeconds: 0,
            remunerationTimeInHours: '0',
            preserveNonBillableHours: false,
            billingUpliftValueFactors: [],
            valueFactorsNotes: '',
            notes: '',
        }),
        []
    );

    const [searchMyCases, setSearchMyCases] = useState(false);
    const [initialFormData, setInitialFormData] = useState<TimecardDto | undefined>();
    // Track which matter IDs have returned 403 errors to prevent infinite refetch loops
    // When keepUnusedDataFor: 0 is set on the query and an error occurs, rapid state changes
    // can cause the cache to be evicted and refetched repeatedly
    const [matterForbiddenId, setMatterForbiddenId] = useState<string | undefined>();

    // The create dialog's route inside it, where the router's is the page behind; the router's own otherwise.
    const { params, inDialog } = useCreateRoute();
    const { invoiceId, assignmentId, matterId, id, contactId } = params;
    const isNewInvoiceTimecard =
        !!invoiceId && !!matterId && (!!assignmentId || assignmentId == undefined) && type === 'add';

    const methods = useForm<TimecardDto>({
        defaultValues: initialFormData ? initialFormData : defaultValues,
        values: initialFormData,
    });
    const { control, setValue, getValues } = methods;
    const { dirtyFields, errors } = useFormState({ control });
    const { user } = useAuth();
    const userId = user?.id;
    const canOverrideTimecardRestrictions = useHasPermissions(['UPDATE.TIMECARDS.OVERRIDE_RESTRICTION']);
    const canCreateTimecardsOnNonAccessibleMatters = useHasPermissions(['CREATE.TIMECARDS.NON_ACCESSIBLE_MATTERS']);

    const [
        currentMatter,
        watchedMatterAssignment,
        employee,
        multiplierTypeCode,
        remunerationCalcType,
        totalTimeInHours,
        billedTimeInHours,
        isNonBillable,
        multiplierRate,
        timecardDate,
    ] = useWatch({
        control,
        name: [
            'matter',
            'matterAssignment',
            'employee',
            'multiplierTypeCode',
            'remunerationCalcType',
            'totalTimeInHours',
            'billedTimeInHours',
            'isNonBillable',
            'multiplierRate',
            'timecardDate',
        ],
    });

    const isPracticeAreaMandatory =
        useRequiredFields().isRequired('TIMECARD', 'PRACTICE_AREA') && !currentMatter?.clientParty?.isInternalClient;

    const { resolveTimecardIncrement, isLoading: isLoadingTimecardIncrement } = useTimecardIncrementResolver();
    const { displayDecimalScale } = useTimecardPrecision();
    // Undefined when increment control is disabled, keeping the inputs on their default stepping.
    const effectiveTimecardIncrement = resolveTimecardIncrement(currentMatter?.timecardIncrement);

    // A timecard added on an invoice starts with the current user on classic's page; the create dialog, the new
    // design's, starts it with Timecard for empty, as one with no prefill does, and the query follows whoever is picked.
    const prefillsInvoiceUser = isNewInvoiceTimecard && !inDialog;
    const employeeQuerySkip = !(employee || prefillWithCurrentUser || prefillsInvoiceUser);
    const employeeQueryFilters =
        (prefillWithCurrentUser && !employee) || prefillsInvoiceUser ? `id==${userId}` : `id==${employee?.id}`;

    const { data: currentEmployeeQueryData, isLoading: isLoadingCurrentEmployeeQueryData } =
        useGetEmployeesListWithFiltersQuery(
            {
                pageSize: 1,
                page: 0,
                filters: employeeQueryFilters,
            },
            { skip: employeeQuerySkip }
        );

    const currentEmployeeData = useMemo<EmployeeListItemMinDto | undefined>(() => {
        return currentEmployeeQueryData?.content.at(0);
    }, [currentEmployeeQueryData]);

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: [
            'TIMECARD_STATUS_TYPE',
            'TIMECARD_ACTIVITY_TYPE',
            'CURRENCY',
            'EXPENSE_TYPE',
            'EXPENSE_STATUS_TYPE',
            'LANGUAGE',
            'MULTIPLIER_TYPE',
            'REMUNERATION_CALC_TYPE',
            'TIMECARD_VALUE_FACTORS',
            'MATTER_STATE_TYPE',
        ],
    });

    const baseMattersFilter = searchMyCases ? `${myCasesFilter};${openCasesFilter}` : openCasesFilter;
    // When launched from a My Portfolio row (/contact/:contactId/timecards/new), restrict
    // the Case/Client picker to that contact's cases.
    const mattersFilter = contactId ? `clientParty.id==${contactId};${baseMattersFilter}` : baseMattersFilter;

    const getTimecardMinDate = useCallback((): Date | undefined => {
        const employmentStartDate: string | undefined = currentEmployeeData?.employmentStartDate;
        return employmentStartDate ? new Date(employmentStartDate) : undefined;
    }, [currentEmployeeData]);

    const validateTimecardDate = useCallback(
        (value: unknown) => {
            const employmentStartDate: string | undefined = currentEmployeeData?.employmentStartDate;

            if (!value || (typeof value !== 'string' && typeof value !== 'number' && !(value instanceof Date))) {
                return Promise.resolve(true);
            }

            // parseISO reads a yyyy-MM-dd string as local midnight; new Date() reads it as UTC midnight.
            const timecardDate = typeof value === 'string' ? parseISO(value) : new Date(value);
            const employmentStart = employmentStartDate ? parseISO(employmentStartDate) : undefined;
            if (employmentStart && isBefore(timecardDate, employmentStart)) {
                return Promise.resolve(
                    t('validation.must-be-date-after', {
                        greaterThan: subDays(employmentStart, 1).toDateString(),
                    })
                );
            }

            if (canOverrideTimecardRestrictions) {
                return Promise.resolve(true);
            }

            const today = new Date();
            const isBeforePreviousMonth = isBefore(
                timecardDate,
                new Date(today.getFullYear(), today.getMonth() - 1, 1)
            );
            const isPreviousMonth = isBefore(timecardDate, new Date(today.getFullYear(), today.getMonth(), 1));

            if (
                currentMatter &&
                currentMatter.isTimecardEntryPreviousMonthBlocked &&
                (isBeforePreviousMonth ||
                    (isPreviousMonth && today.getDate() >= currentMatter.timecardEntryDeadlineWithinCurrentMonth))
            ) {
                return Promise.resolve(t('validation.timecard-entry-previous-months-blocked'));
            }

            if (currentMatter?.isTimecardEntryPreviousWeekBlocked) {
                const startOfCurrentWeek = startOfISOWeek(today);
                const deadlineIsoDay =
                    Object.values(DayOfWeek).indexOf(currentMatter.timecardEntryDeadlineWithinCurrentWeek) + 1;
                if (
                    isBefore(timecardDate, subWeeks(startOfCurrentWeek, 1)) ||
                    (isBefore(timecardDate, startOfCurrentWeek) && getISODay(today) >= deadlineIsoDay)
                ) {
                    return Promise.resolve(t('validation.timecard-entry-previous-weeks-blocked'));
                }
            }

            return Promise.resolve(true);
        },
        [currentEmployeeData, currentMatter, canOverrideTimecardRestrictions]
    );

    const status = useMemo(
        () =>
            isNewInvoiceTimecard
                ? TimecardStatusType.DRAFT_INVOICE
                : (timecard?.timecardStatusTypeCode as TimecardStatusType) || TimecardStatusType.DRAFT,
        [isNewInvoiceTimecard, timecard?.timecardStatusTypeCode]
    );

    // Skip the matter query if we've already received a 403 for this specific matter
    // This prevents infinite refetch loops caused by keepUnusedDataFor: 0 + rapid state changes
    const shouldSkipMatterQuery = !matterId || matterForbiddenId === matterId;

    const {
        data: matterPrefilledFull,
        isLoading: isLoadingMatterPrefilledFull,
        isError: isErrorMatterPrefilled,
        error: matterPrefilledError,
        refetch: refetchMatter,
    } = useGetMatterByIdQuery(shouldSkipMatterQuery ? skipToken : matterId, {
        // Disable all automatic refetch behaviors to prevent infinite loop when matter returns 403
        refetchOnFocus: false,
        refetchOnReconnect: false,
        refetchOnMountOrArgChange: false,
    });

    // Track 403 errors to prevent infinite refetch loops
    // Use useLayoutEffect to set the forbidden ID synchronously before the next render
    // to prevent even a single additional refetch after the 403 error
    useLayoutEffect(() => {
        if (isErrorMatterPrefilled && matterId) {
            const errorStatus =
                matterPrefilledError && 'status' in matterPrefilledError ? matterPrefilledError.status : undefined;
            if (errorStatus === 403 && matterForbiddenId !== matterId) {
                // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- records the query's 403 so the next render skips it, before it can refetch
                setMatterForbiddenId(matterId);
            }
        }
    }, [isErrorMatterPrefilled, matterId, matterPrefilledError, matterForbiddenId]);

    // Fallback to restricted-info endpoint when the main matter query returns 403
    // This allows prefilling the matter dropdown even for restricted cases
    const shouldFetchRestrictedInfo = !!matterId && matterForbiddenId === matterId;
    const { data: matterPrefilledRestricted, isLoading: isLoadingMatterPrefilledRestricted } =
        useGetMatterRestrictedInfoQuery(shouldFetchRestrictedInfo ? matterId : skipToken, {
            refetchOnFocus: false,
            refetchOnReconnect: false,
            refetchOnMountOrArgChange: false,
        });

    // Use full matter data if available, otherwise fall back to restricted info
    const matterPrefilled = matterPrefilledFull ?? matterPrefilledRestricted;
    const isLoadingMatterPrefilled = isLoadingMatterPrefilledFull || isLoadingMatterPrefilledRestricted;

    const practiceAreasQuerySkip = !currentMatter;

    const { data: lastAddedTimecardPracticeAreas } = useGetLastAddedTimecardPracticeAreasQuery(
        {
            employeeId: userId?.toString() || '',
            matterId: currentMatter?.id || 0,
        },
        { skip: practiceAreasQuerySkip }
    );

    const showEmptySelection = useIsFeatureEnabled(FeatureFlag.SHOW_EMPTY_SELECTION);
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);
    // Same rule for the dropdown's group heading and the client line under the field, so they stay in step.
    const partyNumberPrefix = (partyNumber?: string | null) =>
        isPartyNumberUsageEnabled && partyNumber?.trim() ? `[${partyNumber.trim()}]` : null;
    const selectedClientName = currentMatter?.clientParty?.name
        ? [partyNumberPrefix(currentMatter.clientParty.partyNumber), currentMatter.clientParty.name]
              .filter(Boolean)
              .join(' ')
        : null;
    const hideDontNullifyOption = useIsFeatureEnabled(FeatureFlag.HIDE_DONT_NULLIFY_OPTION);
    const isZeroHoursAllowedOnDraftTc = useIsFeatureEnabled(FeatureFlag.ZERO_HOURS_ALLOWED_ON_DRAFT_TC);
    const showPreviousTimecardEntry = useIsFeatureEnabled(FeatureFlag.SHOW_PREVIOUS_TIMECARD_ENTRY_IN_TC_FORM);
    const isMultiAuthorEnabled = useIsFeatureEnabled(FeatureFlag.CREATE_MULTIAUTHOR_TIMECARD);
    const isBilledTimeControlEnabled = useIsFeatureEnabled(FeatureFlag.TIMECARD_BILLED_TIME_CONTROL);
    const isTimecardNotesEnabled = useIsFeatureEnabled(FeatureFlag.TIMECARD_NOTES);
    const canUpdateBilledTime = useHasPermissions(['UPDATE.TIMECARD.BILLED_TIME']);
    const canUpdateBilledTimeBelowUsedTime = useHasPermissions(['UPDATE.TIMECARD.BILLED_TIME_BELOW_USED_TIME']);
    const isMultiSelectMode =
        !inPlace &&
        isMultiAuthorEnabled &&
        (type === 'add' || (type === 'edit' && status === TimecardStatusType.DRAFT));
    const watchedEmployees: NamedEntityMinDto[] | undefined = useWatch({ control, name: 'employees' });
    const hasMultipleEmployees = isMultiSelectMode && (watchedEmployees?.length ?? 0) > 1;
    const costAuthors = useMemo(
        () => (isMultiSelectMode ? (watchedEmployees ?? []) : employee ? [employee] : []),
        [isMultiSelectMode, watchedEmployees, employee]
    );

    const { data: lastTimecardCreatedAt } = useGetLastTimecardCreatedAtQuery(
        { employeeId: employee?.id as number },
        { skip: !showPreviousTimecardEntry || !employee?.id || type !== 'add' }
    );

    // A duplicated timecard starts without an author (the copy is as often for someone else as for
    // oneself), so the author field offers a shortcut to fill in the current user instead of hunting
    // for their own name in the dropdown. Fetched with the same args as the prefill query above, so
    // it shares that cache entry rather than issuing a second request.
    const isAuthorEmpty = isMultiSelectMode ? !watchedEmployees?.length : !employee;
    const { data: currentUserEmployeeQueryData } = useGetEmployeesListWithFiltersQuery(
        {
            pageSize: 1,
            page: 0,
            filters: `id==${userId}`,
        },
        { skip: !userId || !isAuthorEmpty }
    );
    const currentUserEmployee = currentUserEmployeeQueryData?.content.at(0);

    const addCurrentUserAsAuthor = useCallback(() => {
        if (!currentUserEmployee) {
            return;
        }
        // The option object is set as-is: rebuilding it as a bare { id, name } would drop the name
        // parts the LAST_NAME_FIRST display order needs.
        setValue('employee', currentUserEmployee, { shouldDirty: true, shouldValidate: true });
        if (isMultiSelectMode) {
            setValue('employees', [currentUserEmployee], { shouldDirty: true, shouldValidate: true });
        }
    }, [currentUserEmployee, isMultiSelectMode, setValue]);

    const addCurrentUserAsAuthorLink =
        isAuthorEmpty && currentUserEmployee ? (
            // A button, so the shortcut is focusable and responds to Enter/Space. The slot is a flex row
            // aligned to the end (where the previous-timecard hint sits), so `mr-auto` absorbs the free
            // space and pulls the shortcut back under the field's left edge.
            <button
                type="button"
                onClick={addCurrentUserAsAuthor}
                className="text-caption text-link mr-auto w-fit cursor-pointer no-underline hover:underline"
            >
                {t('timecards.form.add-me-as-author')}
            </button>
        ) : undefined;

    const effectiveIsNonBillable = useMemo(() => {
        if (watchedMatterAssignment) {
            return watchedMatterAssignment.isNonBillable ?? false;
        }

        return currentMatter?.project ? currentMatter.project.isNonBillable : currentMatter?.isNonBillable;
    }, [currentMatter, watchedMatterAssignment]);

    const effectivePreserveNonBillableHours = useMemo(() => {
        if (watchedMatterAssignment) {
            return watchedMatterAssignment.preserveNonBillableHours ?? false;
        }

        return currentMatter?.project
            ? currentMatter.project.preserveNonBillableHours
            : currentMatter?.preserveNonBillableHours;
    }, [currentMatter, watchedMatterAssignment]);

    const canEditBilledTime =
        isBilledTimeControlEnabled &&
        (canUpdateBilledTime || canUpdateBilledTimeBelowUsedTime) &&
        !isNonBillable &&
        !effectiveIsNonBillable;

    const usedTimeInHours = parseFloat(String(totalTimeInHours ?? '0').replace(',', '.') || '0');

    // The capped privilege unlocks the field but not the values above the used time; the uncapped one
    // wins when a user holds both.
    const billedTimeCapInHours = canEditBilledTime && !canUpdateBilledTime ? usedTimeInHours : undefined;

    // The uplift block explains a billed time above the used time, so it follows the live hours rather
    // than what was saved — typing a bigger billed time reveals it, pulling it back down hides it (and
    // the backend drops the stored factors to match).
    const showBillingUpliftFields =
        isBilledTimeControlEnabled &&
        parseFloat(String(billedTimeInHours ?? '0').replace(',', '.') || '0') > usedTimeInHours;

    const billingUpliftValueFactorOptions = useMemo(
        () =>
            (getValidOptionsFromClassifier(classifiers['TIMECARD_VALUE_FACTORS']) as {
                label: string;
                value: string;
            }[]) ?? [],
        [classifiers]
    );

    // Track the last matter ID for which we reset to billable, to avoid repeated resets.
    const lastBillableResetMatterIdRef = useRef<number | undefined>(initialData?.matter?.id);

    // TODO: cascading-set-state skip — RHF `setValue` framework calls for related billable/non-billable fields driven by matter change.
    useEffect(() => {
        if (isNewInvoiceTimecard) {
            return;
        }

        if (effectiveIsNonBillable) {
            const nextPreserve = effectivePreserveNonBillableHours ?? false;
            const nextBilledSeconds = effectivePreserveNonBillableHours ? getValues('totalTimeInSeconds') : 0;
            const nextBilledHours = effectivePreserveNonBillableHours ? getValues('totalTimeInHours') : '0';
            // Every setValue notifies the form's watchers, and this effect depends on watched values
            // that come back as fresh references on each notification. Writing unconditionally
            // re-enters the effect and never converges, so only write what actually differs.
            if (getValues('isNonBillable') !== true) {
                setValue('isNonBillable', true);
            }
            if (getValues('preserveNonBillableHours') !== nextPreserve) {
                setValue('preserveNonBillableHours', nextPreserve);
            }
            if (getValues('billedTimeInSeconds') !== nextBilledSeconds) {
                setValue('billedTimeInSeconds', nextBilledSeconds);
            }
            if (getValues('billedTimeInHours') !== nextBilledHours) {
                setValue('billedTimeInHours', nextBilledHours);
            }
            // Clear the reset tracking when switching to non-billable matter
            lastBillableResetMatterIdRef.current = undefined;
        } else if (currentMatter && lastBillableResetMatterIdRef.current !== currentMatter.id) {
            // When moving to a billable matter, preserve the existing non-billable flag
            lastBillableResetMatterIdRef.current = currentMatter.id;
        }
    }, [
        isNewInvoiceTimecard,
        effectiveIsNonBillable,
        effectivePreserveNonBillableHours,
        currentMatter,
        setValue,
        getValues,
    ]);

    useEffect(() => {
        if (
            isLoadingCurrentEmployeeQueryData ||
            isLoadingMatterPrefilled ||
            isLoadingTimecardIncrement ||
            initialFormData
        ) {
            return;
        }
        let finalInitialData = { ...defaultValues, ...initialData } as TimecardDto;

        // prefill employee
        if ((prefillWithCurrentUser || prefillsInvoiceUser) && currentEmployeeData) {
            finalInitialData = {
                ...finalInitialData,
                employee: currentEmployeeData,
                employees: isMultiSelectMode ? [currentEmployeeData] : undefined,
            };
        }

        // prefill matter
        if (matterPrefilled && !watchedMatterAssignment) {
            finalInitialData = {
                ...finalInitialData,
                matter: matterPrefilled,
                remunerationCalcType: matterPrefilled.project
                    ? matterPrefilled.project.remunerationCalcTypeCode
                    : matterPrefilled.remunerationCalcTypeCode,
                isNonBillable: isNewInvoiceTimecard
                    ? false
                    : matterPrefilled.project
                      ? matterPrefilled.project.isNonBillable
                      : matterPrefilled.isNonBillable,
                preserveNonBillableHours: isNewInvoiceTimecard
                    ? false
                    : matterPrefilled.project
                      ? matterPrefilled.project.preserveNonBillableHours
                      : matterPrefilled.preserveNonBillableHours,
            };
        }

        if (
            initialData?.matterAssignment
                ? initialData.matterAssignment.isNonBillable && !initialData.matterAssignment.preserveNonBillableHours
                : initialData?.matter?.project
                  ? initialData.matter.project.isNonBillable && !initialData.matter.project.preserveNonBillableHours
                  : initialData?.matter?.isNonBillable && !initialData.matter.preserveNonBillableHours
        ) {
            finalInitialData = {
                ...finalInitialData,
                isNonBillable: true,
                billedTimeInSeconds: 0,
                billedTimeInHours: '0',
            };
        }

        // Loaded hours are replaced with the nearest multiple of the effective increment above the loaded
        // seconds, keeping the seconds fields in sync with the new hours. The seconds, not the hours: those
        // are already rounded up to 0.1h, and a second ceiling overshoots (850s is 0.3h, then 0.5h, not 0.25h).
        // In place only the shown hours are rounded: the stored seconds stay, so a section save that does not
        // edit the hours leaves them as they are.
        const initialIncrement = resolveTimecardIncrement(finalInitialData.matter?.timecardIncrement);
        if (initialIncrement !== undefined) {
            const roundHoursUpToIncrement = (
                hours: string | null | undefined,
                seconds: number | null | undefined
            ): string | null => {
                const loadedHours = parseFloat(hours ?? '');
                if (isNaN(loadedHours)) {
                    return null;
                }
                // Hundredths nest inside every increment grid, so ceiling to them first changes nothing.
                const rounded = roundUpToIncrement(
                    seconds != null ? secondsToHours(seconds, 2) : loadedHours,
                    initialIncrement
                );
                return rounded === loadedHours ? null : rounded.toFixed(getIncrementDecimalScale(initialIncrement));
            };
            const roundedTotal = roundHoursUpToIncrement(
                finalInitialData.totalTimeInHours,
                finalInitialData.totalTimeInSeconds
            );
            if (roundedTotal !== null) {
                finalInitialData = {
                    ...finalInitialData,
                    totalTimeInHours: roundedTotal,
                    totalTimeInSeconds: inPlace
                        ? finalInitialData.totalTimeInSeconds
                        : hoursToSeconds(parseFloat(roundedTotal)),
                };
            }
            const roundedBilled = roundHoursUpToIncrement(
                finalInitialData.billedTimeInHours,
                finalInitialData.billedTimeInSeconds
            );
            if (roundedBilled !== null) {
                finalInitialData = {
                    ...finalInitialData,
                    billedTimeInHours: roundedBilled,
                    billedTimeInSeconds: inPlace
                        ? finalInitialData.billedTimeInSeconds
                        : hoursToSeconds(parseFloat(roundedBilled)),
                };
            }
        }

        // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- seeds the form once its prefill queries have answered
        setInitialFormData(finalInitialData);
    }, [
        initialData,
        initialFormData,
        isLoadingCurrentEmployeeQueryData,
        isLoadingMatterPrefilled,
        isLoadingTimecardIncrement,
        currentEmployeeData,
        prefillWithCurrentUser,
        prefillsInvoiceUser,
        matterPrefilled,
        isNewInvoiceTimecard,
        defaultValues,
        watchedMatterAssignment,
        isMultiSelectMode,
        resolveTimecardIncrement,
        inPlace,
    ]);

    useEffect(() => {
        const practiceAreas = getValues('practiceAreas');
        if (lastAddedTimecardPracticeAreas?.length && (type === 'add' || !practiceAreas?.length)) {
            setValue('practiceAreas', lastAddedTimecardPracticeAreas);
        }
    }, [getValues, lastAddedTimecardPracticeAreas, setValue, type]);

    const onIsNonBillableChanged = useCallback(
        (isChecked: boolean) => {
            const preserveNonBillableHours = getValues('preserveNonBillableHours');
            if (isChecked) {
                if (!preserveNonBillableHours) {
                    setValue('billedTimeInSeconds', 0);
                    setValue('billedTimeInHours', '0');
                } else {
                    setValue('billedTimeInSeconds', getValues('totalTimeInSeconds'));
                    setValue('billedTimeInHours', getValues('totalTimeInHours'));
                }
            } else {
                setValue('billedTimeInSeconds', getValues('totalTimeInSeconds'));
                setValue('billedTimeInHours', getValues('totalTimeInHours'));
                setValue('preserveNonBillableHours', false);
            }
        },
        [getValues, setValue]
    );

    const onPreserveNonBillableHoursChanged = useCallback(
        (isChecked: boolean) => {
            if (isChecked) {
                setValue('billedTimeInSeconds', getValues('totalTimeInSeconds'));
                setValue('billedTimeInHours', getValues('totalTimeInHours'));
            } else {
                setValue('billedTimeInSeconds', 0);
                setValue('billedTimeInHours', '0');
            }
        },
        [getValues, setValue]
    );

    // Typed hours that land back on the unedited value restore its stored seconds. In place the unedited value is
    // the rounded hours the form was seeded with, under the stored seconds; the full form compares with the loaded
    // record, as it always has.
    const hoursBaseline = inPlace ? initialFormData : initialData;

    const onTotalTimeChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            const hoursValue = event.target.value;

            let secondsValue: number | null = null;
            if (hoursValue && hoursValue.length > 0) {
                // eslint-disable-next-line no-useless-escape
                const hours = parseFloat(hoursValue.split(/[,\.]/).filter(Boolean).join('.'));
                secondsValue = hoursToSeconds(hours);
            }
            if (parseFloat(hoursValue || '0') === parseFloat(hoursBaseline?.totalTimeInHours || '0')) {
                setValue('totalTimeInSeconds', hoursBaseline?.totalTimeInSeconds || 0);
            } else {
                setValue('totalTimeInSeconds', secondsValue);
            }
        },
        [hoursBaseline?.totalTimeInHours, hoursBaseline?.totalTimeInSeconds, setValue]
    );

    const onTotalTimeBlur = useCallback(
        (val: string | undefined | null) => {
            const isNonBillable = getValues('isNonBillable');
            const preserveNonBillableHours = getValues('preserveNonBillableHours');
            const value = !val || val === null ? '' : val;
            if (!isNonBillable || preserveNonBillableHours) {
                let billedTimeInSeconds: number;
                let billedTimeInHours: string | null = value;
                if (value === '') {
                    billedTimeInSeconds = 0;
                    billedTimeInHours = null;
                } else {
                    billedTimeInSeconds = hoursToSeconds(parseFloat(billedTimeInHours));
                }
                if (parseFloat(billedTimeInHours || '0') === parseFloat(hoursBaseline?.totalTimeInHours || '0')) {
                    billedTimeInSeconds = hoursBaseline?.totalTimeInSeconds ?? 0;
                }
                setValue('totalTimeInSeconds', billedTimeInSeconds);
                setValue('totalTimeInHours', billedTimeInHours);
                setValue('billedTimeInSeconds', billedTimeInSeconds);
                setValue('billedTimeInHours', billedTimeInHours);
            }
        },
        [getValues, hoursBaseline?.totalTimeInHours, hoursBaseline?.totalTimeInSeconds, setValue]
    );

    const onBilledTimeChange = useCallback(
        (hoursValue: string | undefined): void => {
            let secondsValue: number | null = 0;
            if (hoursValue && hoursValue.length > 0) {
                // eslint-disable-next-line no-useless-escape
                const hours = parseFloat(hoursValue.split(/[,\.]/).filter(Boolean).join('.'));
                secondsValue = hoursToSeconds(hours);
            }

            if (parseFloat(hoursValue || '0') === parseFloat(hoursBaseline?.billedTimeInHours || '0')) {
                setValue('billedTimeInSeconds', hoursBaseline?.billedTimeInSeconds ?? 0);
            } else {
                setValue('billedTimeInSeconds', secondsValue);
            }
        },
        [hoursBaseline?.billedTimeInHours, hoursBaseline?.billedTimeInSeconds, setValue]
    );

    const onRemunerationTimeChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            const hoursValue = event.target.value;

            let secondsValue: number | null = null;
            if (hoursValue && hoursValue.length > 0) {
                // eslint-disable-next-line no-useless-escape
                const hours = parseFloat(hoursValue.split(/[,\.]/).filter(Boolean).join('.'));
                secondsValue = hoursToSeconds(hours);
            }

            setValue('remunerationTimeInSeconds', secondsValue);
        },
        [setValue]
    );

    const handleOnMatterAssignemntChange = useCallback(
        (value: MatterAssignmentMinDto | null) => {
            if (value && !isNewInvoiceTimecard) {
                if (value.isNonBillable) {
                    // Non-billable assignment forces non-billable
                    setValue('isNonBillable', true);
                    setValue('preserveNonBillableHours', value.preserveNonBillableHours ?? false);
                    setValue(
                        'billedTimeInSeconds',
                        value.preserveNonBillableHours ? getValues('totalTimeInSeconds') : 0
                    );
                    setValue('billedTimeInHours', value.preserveNonBillableHours ? getValues('totalTimeInHours') : '0');
                } else if (!getValues('isNonBillable')) {
                    // Billable assignment + billable TC: set full billed time
                    setValue('preserveNonBillableHours', false);
                    setValue('billedTimeInSeconds', getValues('totalTimeInSeconds'));
                    setValue('billedTimeInHours', getValues('totalTimeInHours'));
                }
                // If assignment is billable but TC is non-billable: preserve current state
            }
        },
        [getValues, setValue, isNewInvoiceTimecard]
    );

    const canReadMatterAssignments = useCanReadMatterAssignments();

    const assignmentsQuerySkip = !currentMatter || !canReadMatterAssignments;

    // `currentData`, not `data`: while the list of a newly picked case loads, `data` still holds the
    // previous case's assignments. A required Assignment field with exactly one such option preselects
    // it, the matter-change effect below clears it as belonging to another case, and the two effects
    // re-enter each other until React aborts the render.
    const { currentData: matterAssignmentsList } = useGetMinAssignmentsListWithFiltersQuery(
        matterAssignmentsArgs(currentMatter?.id || '0'),
        { skip: assignmentsQuerySkip }
    );

    useEffect(() => {
        if (isNewInvoiceTimecard && matterAssignmentsList?.content && matterAssignmentsList.content.length > 0) {
            const matterAssignment = matterAssignmentsList.content.find((item) => item.id === Number(assignmentId));
            if (matterAssignment) {
                setValue('matterAssignment', matterAssignment);
            }
        }
    }, [isNewInvoiceTimecard, matterAssignmentsList, assignmentId, setValue]);

    const [showAssignmentSelectInput, setShowAssignmentSelectInput] = useState(
        !!initialData?.matterAssignment ||
            Boolean(matterAssignmentsList?.content && matterAssignmentsList.content.length > 0 && currentMatter)
    );

    useEffect(() => {
        // Boolean(): the `&&` chain yields `currentMatter` itself when truthy, and that object is a
        // fresh reference on every render, so storing it would never compare equal and the state
        // would re-render forever. Every reader treats this as a flag.
        const shouldShow =
            !!watchedMatterAssignment ||
            Boolean(matterAssignmentsList?.content && matterAssignmentsList.content.length > 0 && currentMatter);
        // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- follows the watched assignment and the fetched assignment list
        setShowAssignmentSelectInput(shouldShow);
    }, [watchedMatterAssignment, matterAssignmentsList, currentMatter]);

    // Check if all assignments are disabled
    const areAllAssignmentsDisabled = useMemo(() => {
        if (!matterAssignmentsList?.content || matterAssignmentsList.content.length === 0 || !timecardDate) {
            return false;
        }
        return matterAssignmentsList.content.every(
            (option) => option.endDate && !isBefore(new Date(timecardDate), new Date(option.endDate))
        );
    }, [matterAssignmentsList, timecardDate]);

    // Group assignment options into "Active" and "Closed" groups
    const groupedAssignmentOptions = useMemo(() => {
        const options = matterAssignmentsList?.content ?? [];
        const activeGroupLabel = t('timecards.form.active-assignments');
        const closedGroupLabel = t('timecards.form.closed-assignments');

        const groupedOptions = options.map((option) => {
            const initialAssignmentId = initialData?.matterAssignment?.id;

            // Keep initially loaded assignment in "Active" group
            if (initialAssignmentId && option.id === initialAssignmentId) {
                return { ...option, group: activeGroupLabel };
            }

            // No endDate means active
            if (!option.endDate || !timecardDate) {
                return { ...option, group: activeGroupLabel };
            }

            const isClosed = !isBefore(new Date(timecardDate), new Date(option.endDate));
            return { ...option, group: isClosed ? closedGroupLabel : activeGroupLabel };
        });

        return groupedOptions.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    }, [matterAssignmentsList, timecardDate, initialData?.matterAssignment?.id]);

    const assignmentGroupSortOrder = useMemo(
        () => [t('timecards.form.active-assignments'), t('timecards.form.closed-assignments')],
        []
    );

    // Clear assignment selection if it becomes disabled when timecardDate changes
    useEffect(() => {
        if (!isNewInvoiceTimecard && watchedMatterAssignment && watchedMatterAssignment.endDate && timecardDate) {
            const initialAssignmentId = initialData?.matterAssignment?.id;
            // Don't clear the initially loaded assignment
            if (initialAssignmentId && watchedMatterAssignment.id === initialAssignmentId) {
                return;
            }
            const isDisabled = !isBefore(new Date(timecardDate), new Date(watchedMatterAssignment.endDate));
            if (isDisabled) {
                setValue('matterAssignment', null);
            }
        }
    }, [timecardDate, watchedMatterAssignment, setValue, initialData, isNewInvoiceTimecard]);

    // Clear assignment selection when matter changes (assignment belongs to a different matter)
    useEffect(() => {
        if (watchedMatterAssignment && currentMatter) {
            const initialAssignmentId = initialData?.matterAssignment?.id;
            const initialMatterId = initialData?.matter?.id;
            // Don't clear the initially loaded assignment if the matter is still the initial matter
            // This prevents clearing during form initialization due to timing issues
            if (
                initialAssignmentId &&
                watchedMatterAssignment.id === initialAssignmentId &&
                currentMatter.id === initialMatterId
            ) {
                return;
            }
            // Check if the assignment belongs to a different matter
            if (watchedMatterAssignment.matter && watchedMatterAssignment.matter.id !== currentMatter.id) {
                setValue('matterAssignment', null);
            }
        }
    }, [currentMatter, watchedMatterAssignment, setValue, initialData]);

    const timecardActivityTypeOptions =
        (getValidOptionsFromClassifier(classifiers['TIMECARD_ACTIVITY_TYPE']) as {
            label: string;
            value: string;
            description: string;
        }[]) ?? [];
    const isTimecardActivityTypeOptions = timecardActivityTypeOptions.length > 0;
    const showActivityTypeSelectInput = isTimecardActivityTypeOptions || showEmptySelection;

    // Drives the activity-type field's `required` rule. Default `true` so the asterisk
    // is always shown and validation always fires — matching the BE rule, which requires
    // the activity type whenever the saved status is anything other than DRAFT.
    //
    // The form page's draft-save handlers (`submitSaveAsDraft`, `submitCreateTimecard`, and
    // the DRAFT branch of `submitUpdateTimecard`, in TimecardPageForm) flip this to `false`
    // via `flushSync` immediately before invoking `handleSubmit`, then restore it in a
    // `finally`. This is the inverse of the convention used for the other fields
    // (status-driven `required` props plus per-action `register(..., { required })` calls),
    // and it is intentional:
    //   - The form's `status` field still equals DRAFT at the moment the user clicks
    //     "Finish", so a status-driven `required` evaluates `false` at validation time
    //     and lets the request through to the BE, which then 400s with
    //     "Activity type is obligatory".
    //   - The field is wrapped in `Controller` (see SelectInput.tsx), whose `rules` prop
    //     takes precedence over imperative `register(name, { required })` calls, so the
    //     per-action `register` pattern used elsewhere is silently a no-op for this field.
    // Tying the rule to the *intended action* (encoded in this state) instead of the
    // *current status* aligns FE validation with BE behaviour.
    // In place, a save keeps the record's status, so the rule is that status's from the start.
    const [activityTypeRequired, setActivityTypeRequired] = useState(!inPlace || status !== TimecardStatusType.DRAFT);

    // The page widens this rule per action before it submits (see its `submit…` handlers); in place the one save
    // keeps the status, so the rule is the one the page's Save / Save as draft registers for it.
    const totalTimeValidate =
        inPlace && (status !== TimecardStatusType.DRAFT || !isZeroHoursAllowedOnDraftTc)
            ? {
                  greaterThanZero: numberIsGreaterThan(0, t('validation.cannot-be-zero-hours')),
                  notExceed24: mustNotExceed(24, t('validation.cannot-exceed-hours', { max: '24' })),
              }
            : isZeroHoursAllowedOnDraftTc
              ? undefined
              : numberIsGreaterThan(0, t('validation.cannot-be-zero-hours'));

    const remunerationCalcTypeOptions = useMemo(() => {
        return (
            (getValidOptionsFromClassifier(classifiers['REMUNERATION_CALC_TYPE']) as {
                label: string;
                value: string;
            }[]) ?? []
        ).map((option) => {
            if (option.value === RemunerationCalcType.EQUALS_BILLED_TIME) {
                return {
                    ...option,
                    label: `${option.label} (${formatToHours(Number(billedTimeInHours), displayDecimalScale)})`,
                };
            } else if (option.value === RemunerationCalcType.EQUALS_USED_TIME) {
                return {
                    ...option,
                    label: `${option.label} (${formatToHours(Number(totalTimeInHours), displayDecimalScale)})`,
                };
            }
            return option;
        });
    }, [classifiers, totalTimeInHours, billedTimeInHours, displayDecimalScale]);

    const isUserPrivilegedToReadRemunerationTime = useHasPermissions([
        'FIELD_ACCESS.TIMECARD.REMUNERATION_TIME',
        'READ.TIMECARDS.REMUNERATION',
    ]);
    const isUserPrivilegedToUpdateRemunerationTime = useHasPermissions([
        'FIELD_ACCESS.TIMECARD.REMUNERATION_TIME',
        'UPDATE.TIMECARDS.REMUNERATION',
    ]);
    const showRemunerationCalcTypeRadioInput =
        useIsFeatureEnabled(FeatureFlag.REMUNERATION_CALC_TYPE_CONTROL) &&
        remunerationCalcTypeOptions.length > 0 &&
        isUserPrivilegedToReadRemunerationTime;

    const multiplierTypes = useMemo(() => {
        const classifier = classifiers['MULTIPLIER_TYPE'];
        if (!classifier) {
            return [];
        }

        return classifier.values
            ? classifier.values
                  .map((classifierValue) => ({
                      label: getClassifierText(classifier, classifierValue.code),
                      value: classifierValue.code,
                      description:
                          classifierValue.code === 'NONE'
                              ? `${getClassifierText(classifier, classifierValue.code)} (${classifierValue.parameters ? parseFloat(classifierValue.parameters['multiplierRate'] ?? '0').toFixed(1) + 'x' : ''})`
                              : `${classifierValue.parameters ? parseFloat(classifierValue.parameters['multiplierRate'] ?? '0').toFixed(1) + 'x' : ''}`,

                      multiplierRate: classifierValue.parameters?.['multiplierRate'] ?? '0',
                  }))
                  .sort((a, b) => parseFloat(a.multiplierRate) - parseFloat(b.multiplierRate))
            : [];
    }, [classifiers]);

    const handleMultiplierTypeCode = (value: string | null) => {
        // Pressing the selected code again clears it, and the form stores that as null.
        setValue('multiplierTypeCode', value as string);
        if (multiplierTypes) {
            const multiplierClassifierValue = multiplierTypes.find((item) => item.value === value);
            setValue('multiplierRate', multiplierClassifierValue?.multiplierRate ?? '');
        }
    };

    const handleTimecardHistoryCopy = useCallback(
        async (timecard: TimecardMatterHistoryItem) => {
            if (!dirtyFields.description || getValues('description').length === 0) {
                setValue('description', timecard.description || '', { shouldDirty: false });
            }
        },
        [dirtyFields, getValues, setValue]
    );

    // Unlike the history copy, picking a template is an explicit choice, so it overwrites whatever the
    // description already holds instead of only filling an empty field. "Add" appends on its own row.
    const handleTimecardTemplateSelect = useCallback(
        (templateText: string, mode: TemplateApplyMode) => {
            const current = getValues('description');
            const next = mode === 'add' && current ? `${current}\n${templateText}` : templateText;
            setValue('description', next, { shouldDirty: true });
        },
        [getValues, setValue]
    );
    return {
        type,
        timecard,
        initialData,
        initialFormData,
        methods,
        errors,
        isLoading:
            isLoadingCurrentEmployeeQueryData || isLoadingClassifiers || isLoadingMatterPrefilled || !initialFormData,
        params: { invoiceId, matterId, id, contactId },
        user,
        status,
        classifiers,
        isNewInvoiceTimecard,
        shouldSkipMatterQuery,
        refetchMatter,
        matterPrefilled,
        currentMatter,
        timecardDate,
        isNonBillable,
        billedTimeInHours,
        multiplierRate,
        multiplierTypeCode,
        remunerationCalcType,
        watchedEmployees,
        isMultiSelectMode,
        hasMultipleEmployees,
        costAuthors,
        mattersFilter,
        searchMyCases,
        setSearchMyCases,
        canCreateTimecardsOnNonAccessibleMatters,
        isPartyNumberUsageEnabled,
        partyNumberPrefix,
        selectedClientName,
        showAssignmentSelectInput,
        areAllAssignmentsDisabled,
        groupedAssignmentOptions,
        assignmentGroupSortOrder,
        handleOnMatterAssignemntChange,
        showActivityTypeSelectInput,
        isTimecardActivityTypeOptions,
        timecardActivityTypeOptions,
        activityTypeRequired,
        setActivityTypeRequired,
        handleTimecardHistoryCopy,
        handleTimecardTemplateSelect,
        isTimecardNotesEnabled,
        addCurrentUserAsAuthorLink,
        showPreviousTimecardEntry,
        lastTimecardCreatedAt,
        getTimecardMinDate,
        validateTimecardDate,
        onIsNonBillableChanged,
        effectiveIsNonBillable,
        hideDontNullifyOption,
        onPreserveNonBillableHoursChanged,
        isZeroHoursAllowedOnDraftTc,
        totalTimeValidate,
        onTotalTimeChange,
        onTotalTimeBlur,
        effectiveTimecardIncrement,
        canEditBilledTime,
        billedTimeCapInHours,
        onBilledTimeChange,
        showBillingUpliftFields,
        billingUpliftValueFactorOptions,
        showRemunerationCalcTypeRadioInput,
        remunerationCalcTypeOptions,
        isUserPrivilegedToUpdateRemunerationTime,
        onRemunerationTimeChange,
        multiplierTypes,
        handleMultiplierTypeCode,
        isPracticeAreaMandatory,
    };
};

export type TimecardFormState = ReturnType<typeof useTimecardForm>;
